import { inject, Service } from '@angular/core';
import { Observable, of } from 'rxjs';
import { first, map, mergeMap, share, tap } from 'rxjs/operators';
import { Api, ApiClient, ApiTypes } from '@app/api';
import { LoadedAccountContext } from '@core/classes/account/account-context.class';
import { AccountService } from '@core/providers/account/account.service';
import { IGroupPayload } from '@shared/interfaces/groups/add-group-payload.interface';
import { IGroupResponse } from '@shared/interfaces/groups/add-group-response.interface';
import { fetchAllPages } from '@shared/utils/fetch-all-pages';

type VenueGroupDropdownItems = Api.fbi.venuegroups.dropdowns.paged.GetResult['items'];

@Service()
export class GroupsBackendService {
  private api = inject(ApiClient);
  private accountService = inject(AccountService);

  private cachedVenueGroups: ApiTypes.VenueGroupV2ResponseDto[] | null = null;
  private venueGroupsRequest$: Observable<ApiTypes.VenueGroupV2ResponseDto[]> | null = null;
  private cachedAccountUuid: string | null = null;
  private cachedVenueGroupForDropdowns: VenueGroupDropdownItems | null = null;
  private venueGroupsForDropdownsRequest$: Observable<VenueGroupDropdownItems> | null = null;

  unassignVenueFromGroup$(venueUuid: string, groupUuid: string): Observable<string> {
    return this.api
      .delete(Api.fbi.venuegroupvenue.byVenueGroupUuid, {
        pathParams: { venueUuid, venueGroupUuid: groupUuid },
      })
      .pipe(map(response => response.uuid));
  }

  assignGroupToVenue$(venueUuid: string, venueGroupUuid: string): Observable<string> {
    return this.api
      .post(Api.fbi.venuegroupvenue, { body: { venueUuid, venueGroupUuid } })
      .pipe(map(response => response.uuid));
  }

  getGroups$(): Observable<ApiTypes.VenueGroupV2ResponseDto[]> {
    return this.callAfter$(data => {
      if (this.cachedVenueGroups && this.cachedAccountUuid === data.uuid) {
        return of(this.cachedVenueGroups);
      }
      if (this.venueGroupsRequest$ && this.cachedAccountUuid === data.uuid) {
        return this.venueGroupsRequest$;
      }

      this.venueGroupsRequest$ = fetchAllPages(page =>
        this.api.get(Api.fbi.venuegroups.paged, {
          pathParams: { uuid: data.uuid },
          query: { page, pageSize: 100 },
        })
      ).pipe(
        tap(response => {
          this.removeCachedGroup();
          this.cachedVenueGroups = response;
          this.cachedAccountUuid = data.uuid;
        }),
        share()
      );

      return this.venueGroupsRequest$;
    });
  }

  getGroupsForDropdown$(): Observable<VenueGroupDropdownItems> {
    return this.callAfter$(data => {
      if (this.cachedVenueGroupForDropdowns && this.cachedAccountUuid === data.uuid) {
        return of(this.cachedVenueGroupForDropdowns);
      }
      if (this.venueGroupsForDropdownsRequest$ && this.cachedAccountUuid === data.uuid) {
        return this.venueGroupsForDropdownsRequest$;
      }

      this.venueGroupsForDropdownsRequest$ = fetchAllPages(page =>
        this.api.get(Api.fbi.venuegroups.dropdowns.paged, {
          pathParams: { uuid: data.uuid },
          query: { page, pageSize: 100 },
        })
      ).pipe(
        tap(response => {
          this.removeCachedGroup();
          this.cachedVenueGroupForDropdowns = response;
          this.cachedAccountUuid = data.uuid;
        }),
        share()
      );

      return this.venueGroupsForDropdownsRequest$;
    });
  }

  addGroup$(data: IGroupPayload): Observable<IGroupResponse> {
    this.removeCachedGroup();

    return this.api.post(Api.fbi.venuegroups, {
      body: data as Api.fbi.venuegroups.PostBody,
    });
  }

  updateGroup$(uuid: string, data: IGroupPayload): Observable<IGroupResponse> {
    this.removeCachedGroup();

    return this.api.put(Api.fbi.venuegroups.byUuid, {
      pathParams: { uuid },
      body: data,
    });
  }

  removeGroup$(uuid: string): Observable<IGroupResponse> {
    this.removeCachedGroup();

    return this.api.delete(Api.fbi.venuegroups.byUuid, { pathParams: { uuid } });
  }

  private removeCachedGroup() {
    this.cachedVenueGroups = null;
    this.cachedVenueGroupForDropdowns = null;
    this.cachedAccountUuid = null;
    this.venueGroupsRequest$ = null;
    this.venueGroupsForDropdownsRequest$ = null;
  }

  private callAfter$<T>(
    apiFunction: (account: LoadedAccountContext) => Observable<T>
  ): Observable<T> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      mergeMap(account => apiFunction(account))
    );
  }
}
